import { Head, Link } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Badge from '@/Components/Badge';
import {
    UsersIcon, QrCodeIcon, UserGroupIcon, EnvelopeIcon,
    ClipboardDocumentCheckIcon, DocumentTextIcon, ClockIcon,
    BuildingOfficeIcon, ExclamationTriangleIcon, ArrowTrendingUpIcon,
    ArrowTrendingDownIcon, ChartBarIcon, CalendarDaysIcon,
    BriefcaseIcon, MapPinIcon,
} from '@heroicons/react/24/outline';
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

/* =============================================
   Stat Card
============================================= */
function StatCard({ icon: Icon, label, value, change, color, href }) {
    const colors = {
        emerald: { bg: 'bg-emerald-50', icon: 'bg-emerald-100 text-emerald-700', ring: 'ring-emerald-600/10' },
        blue:    { bg: 'bg-blue-50',    icon: 'bg-blue-100 text-blue-700',       ring: 'ring-blue-600/10' },
        purple:  { bg: 'bg-purple-50',  icon: 'bg-purple-100 text-purple-700',   ring: 'ring-purple-600/10' },
        amber:   { bg: 'bg-amber-50',   icon: 'bg-amber-100 text-amber-700',     ring: 'ring-amber-600/10' },
        rose:    { bg: 'bg-rose-50',    icon: 'bg-rose-100 text-rose-700',       ring: 'ring-rose-600/10' },
        cyan:    { bg: 'bg-cyan-50',    icon: 'bg-cyan-100 text-cyan-700',       ring: 'ring-cyan-600/10' },
        slate:   { bg: 'bg-slate-50',   icon: 'bg-slate-100 text-slate-700',     ring: 'ring-slate-600/10' },
        indigo:  { bg: 'bg-indigo-50',  icon: 'bg-indigo-100 text-indigo-700',   ring: 'ring-indigo-600/10' },
    };
    const c = colors[color] || colors.emerald;

    const content = (
        <div className={`relative overflow-hidden rounded-2xl border border-slate-200/80 ${c.bg} p-5 transition-all hover:shadow-md hover:-translate-y-0.5 group`}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
                    <p className="text-3xl font-extrabold text-slate-800 mt-1.5 tracking-tight">{(value ?? 0).toLocaleString()}</p>
                    {change !== undefined && change !== null && (
                        <div className={`flex items-center gap-1 mt-2 text-xs font-semibold ${change >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {change >= 0 ? <ArrowTrendingUpIcon className="w-3.5 h-3.5" /> : <ArrowTrendingDownIcon className="w-3.5 h-3.5" />}
                            {change >= 0 ? '+' : ''}{change}% vs last month
                        </div>
                    )}
                </div>
                <div className={`w-11 h-11 rounded-xl ${c.icon} ring-1 ${c.ring} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
            {/* Decorative corner gradient */}
            <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
    );

    return href ? <Link href={href}>{content}</Link> : content;
}

/* =============================================
   Section Header
============================================= */
function SectionHeader({ icon: Icon, title, subtitle }) {
    return (
        <div className="flex items-center gap-2 mb-4">
            {Icon && <Icon className="w-5 h-5 text-emerald-600" />}
            <div>
                <h3 className="text-sm font-bold text-slate-800">{title}</h3>
                {subtitle && <p className="text-[11px] text-slate-400">{subtitle}</p>}
            </div>
        </div>
    );
}

/* =============================================
   Custom Tooltip
============================================= */
function ChartTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-lg p-3 text-xs">
            <p className="font-bold text-slate-700 mb-1.5">{label}</p>
            {payload.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                    <span className="text-slate-500">{p.name}:</span>
                    <span className="font-bold text-slate-700">{p.value}</span>
                </div>
            ))}
        </div>
    );
}

/* =============================================
   Activity Icon
============================================= */
function ActivityIcon({ type }) {
    const map = {
        visitor:   { icon: UsersIcon,                     bg: 'bg-emerald-100 text-emerald-700' },
        material:  { icon: ClipboardDocumentCheckIcon,    bg: 'bg-blue-100 text-blue-700' },
        report:    { icon: DocumentTextIcon,              bg: 'bg-purple-100 text-purple-700' },
        timesheet: { icon: ClockIcon,                     bg: 'bg-amber-100 text-amber-700' },
    };
    const m = map[type] || map.visitor;
    const Icon = m.icon;
    return (
        <div className={`w-8 h-8 rounded-lg ${m.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-4 h-4" />
        </div>
    );
}

/* =============================================
   COLORS
============================================= */
const CHART_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

/* =============================================
   Dashboard
============================================= */
export default function Dashboard({
    stats = {},
    monthlyTrends = [],
    materialsByStatus = [],
    suppliersByCountry = [],
    visitorsByType = [],
    expiringDocuments = [],
    recentActivities = [],
    weeklyHours = [],
}) {
    const s = stats;

    return (
        <AppLayout>
            <Head title="Dashboard" />

            {/* Page header */}
            <div className="mb-8">
                <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Dashboard</h1>
                <p className="text-slate-500 text-sm mt-1">Real-time overview of your operations</p>
            </div>

            {/* ====== STAT CARDS ====== */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 mb-8">
                <StatCard icon={UsersIcon}                  label="Visitors"      value={s.visitors}   change={s.visitorsChange} color="emerald" href="/visitors" />
                <StatCard icon={BuildingOfficeIcon}         label="Suppliers"     value={s.suppliers}  color="blue"    href="/suppliers" />
                <StatCard icon={UserGroupIcon}              label="Customers"     value={s.customers}  color="purple"  href="/customers" />
                <StatCard icon={ClipboardDocumentCheckIcon} label="Requisitions"  value={s.materials}  color="amber"   href="/materials" />
                <StatCard icon={DocumentTextIcon}           label="Daily Reports" value={s.dailyReports} color="cyan"  href="/daily-reports" />
                <StatCard icon={ClockIcon}                  label="Timesheets"    value={s.timesheets} color="indigo"  href="/timesheets" />
                <StatCard icon={BriefcaseIcon}              label="Employees"     value={s.employees}  color="rose"    href="/employees" />
                <StatCard icon={QrCodeIcon}                 label="QR Codes"      value={s.qrCodes}    color="slate"   href="/qr-codes" />
            </div>

            {/* ====== CHARTS ROW 1 ====== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Monthly Trends — Area chart */}
                <div className="lg:col-span-2">
                    <Card>
                        <CardBody>
                            <SectionHeader icon={ChartBarIcon} title="Monthly Trends" subtitle="Last 6 months activity across modules" />
                            <div className="h-72 -mx-2">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={monthlyTrends} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="gMaterials" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="gReports" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                        <Tooltip content={<ChartTooltip />} />
                                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                        <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#10b981" fill="url(#gVisitors)" strokeWidth={2.5} dot={false} activeDot={{ r: 4, strokeWidth: 2 }} />
                                        <Area type="monotone" dataKey="materials" name="Requisitions" stroke="#f59e0b" fill="url(#gMaterials)" strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 2 }} />
                                        <Area type="monotone" dataKey="reports" name="Reports" stroke="#8b5cf6" fill="url(#gReports)" strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 2 }} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                {/* Material Status — Donut */}
                <Card>
                    <CardBody>
                        <SectionHeader icon={ClipboardDocumentCheckIcon} title="Requisitions Status" subtitle="Material request breakdown" />
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={materialsByStatus.filter(m => m.value > 0)}
                                        cx="50%" cy="50%"
                                        innerRadius={55} outerRadius={80}
                                        paddingAngle={4}
                                        dataKey="value"
                                        nameKey="name"
                                        strokeWidth={0}
                                    >
                                        {materialsByStatus.filter(m => m.value > 0).map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(value, name) => [value, name]}
                                        contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex justify-center gap-4 -mt-2">
                            {materialsByStatus.map((m, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-xs">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.color }} />
                                    <span className="text-slate-500">{m.name}</span>
                                    <span className="font-bold text-slate-700">{m.value}</span>
                                </div>
                            ))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* ====== CHARTS ROW 2 ====== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Timesheet Hours — Bar chart */}
                <div className="lg:col-span-2">
                    <Card>
                        <CardBody>
                            <SectionHeader icon={ClockIcon} title="Timesheet Hours" subtitle="Recent daily attendance hours" />
                            <div className="h-64 -mx-2">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={weeklyHours} margin={{ top: 5, right: 10, left: -20, bottom: 0 }} barGap={2}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                        <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                        <Tooltip content={<ChartTooltip />} />
                                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                        <Bar dataKey="ord" name="ORD Hours" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={32} />
                                        <Bar dataKey="ot" name="Overtime" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={32} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            {/* Summary strip */}
                            <div className="flex gap-6 mt-2 pt-3 border-t border-slate-100">
                                <div className="text-xs">
                                    <span className="text-slate-400">Total ORD:</span>{' '}
                                    <span className="font-bold text-emerald-700">{s.totalOrdHours} hrs</span>
                                </div>
                                <div className="text-xs">
                                    <span className="text-slate-400">Total OT:</span>{' '}
                                    <span className="font-bold text-amber-600">{s.totalOtHours} hrs</span>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                {/* Suppliers by Country — horizontal bar */}
                <Card>
                    <CardBody>
                        <SectionHeader icon={BuildingOfficeIcon} title="Suppliers by Country" subtitle="Top countries" />
                        <div className="space-y-3 mt-2">
                            {suppliersByCountry.length > 0 ? suppliersByCountry.map((item, i) => {
                                const maxVal = Math.max(...suppliersByCountry.map(s => s.value), 1);
                                return (
                                    <div key={i}>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className="font-medium text-slate-700 truncate">{item.name || 'Unknown'}</span>
                                            <span className="font-bold text-slate-800">{item.value}</span>
                                        </div>
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${(item.value / maxVal) * 100}%`,
                                                    backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            }) : (
                                <p className="text-xs text-slate-400 text-center py-6">No supplier data</p>
                            )}
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* ====== BOTTOM ROW ====== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Activity */}
                <div className="lg:col-span-2">
                    <Card>
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="font-bold text-slate-800 text-sm">Recent Activity</h3>
                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Live Feed</span>
                        </div>
                        <CardBody className="p-0">
                            <div className="divide-y divide-slate-50">
                                {recentActivities?.length > 0 ? recentActivities.map((a, i) => (
                                    <div key={i} className="flex items-center gap-3 px-6 py-3.5 hover:bg-slate-50/60 transition-colors">
                                        <ActivityIcon type={a.icon} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-800 truncate">{a.title}</p>
                                            <p className="text-xs text-slate-400 truncate">{a.desc}</p>
                                        </div>
                                        <span className="text-[11px] text-slate-400 whitespace-nowrap font-medium">
                                            {a.time ? new Date(a.time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}
                                        </span>
                                    </div>
                                )) : (
                                    <div className="px-6 py-10 text-center text-slate-400 text-sm">No recent activity</div>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </div>

                {/* Right column — quick stats + expiring docs */}
                <div className="space-y-6">
                    {/* Quick Numbers */}
                    <Card>
                        <CardBody>
                            <SectionHeader icon={CalendarDaysIcon} title="This Month" subtitle="Current month snapshot" />
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                                    <p className="text-2xl font-extrabold text-emerald-700">{s.reportsThisMonth ?? 0}</p>
                                    <p className="text-[10px] font-semibold text-emerald-600/70 uppercase">Reports</p>
                                </div>
                                <div className="bg-indigo-50 rounded-xl p-3 text-center">
                                    <p className="text-2xl font-extrabold text-indigo-700">{s.timesheetsThisMonth ?? 0}</p>
                                    <p className="text-[10px] font-semibold text-indigo-600/70 uppercase">Timesheets</p>
                                </div>
                                <div className="bg-amber-50 rounded-xl p-3 text-center">
                                    <p className="text-2xl font-extrabold text-amber-700">{s.pendingMaterials ?? 0}</p>
                                    <p className="text-[10px] font-semibold text-amber-600/70 uppercase">Pending Req.</p>
                                </div>
                                <div className="bg-blue-50 rounded-xl p-3 text-center">
                                    <p className="text-2xl font-extrabold text-blue-700">{s.emails ?? 0}</p>
                                    <p className="text-[10px] font-semibold text-blue-600/70 uppercase">Emails Sent</p>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Expiring Documents */}
                    {expiringDocuments?.length > 0 && (
                        <Card>
                            <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2">
                                <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
                                <h3 className="font-bold text-slate-800 text-sm">Expiring Soon</h3>
                                <Badge variant="warning" className="ml-auto">{expiringDocuments.length}</Badge>
                            </div>
                            <CardBody className="p-0">
                                <div className="divide-y divide-slate-50 max-h-52 overflow-y-auto">
                                    {expiringDocuments.map((doc, i) => (
                                        <div key={i} className="px-6 py-2.5">
                                            <p className="text-xs font-semibold text-slate-800">{doc.document_type}</p>
                                            <p className="text-[11px] text-slate-400">{doc.company_name}</p>
                                            <p className="text-[11px] text-amber-600 font-semibold mt-0.5">
                                                Expires {new Date(doc.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {/* Customers Quick */}
                    <Card>
                        <CardBody>
                            <SectionHeader icon={UserGroupIcon} title="Customers" />
                            <div className="flex items-center justify-between py-1.5 text-sm">
                                <span className="text-slate-500">Active</span>
                                <Badge variant="success">{s.activeCustomers ?? 0}</Badge>
                            </div>
                            <div className="flex items-center justify-between py-1.5 text-sm">
                                <span className="text-slate-500">Pending</span>
                                <Badge variant="warning">{s.pendingCustomers ?? 0}</Badge>
                            </div>
                            <div className="flex items-center justify-between py-1.5 text-sm border-t border-slate-100 mt-1 pt-2">
                                <span className="text-slate-500 font-semibold">Total</span>
                                <span className="font-extrabold text-slate-800">{s.customers ?? 0}</span>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </div>
        </AppLayout>
    );
}
